import { memo, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { clsx } from 'clsx';
import highlightParse from 'autosuggest-highlight/parse';
import highlightMatch from 'autosuggest-highlight/match';
import { HStack, Text, VStack } from '@hst-horizon/horizon-ui';
import UnhandledError from '../../../../../core/components/UnhandledError';
import { ProgressSpinner } from '../../../../../core/components/ProgressIndicators';
import CurrentStoreLink from '../../../../../core/components/router/CurrentStoreLink';
import BrandImage from '../../../../../brand/components/BrandImage';
import type { SearchResultProduct } from '../../../../../product/queries/useProductSearch';
import useProductDetailsLink from '../../../../../product/hooks/useProductDetailsLink';
import ProductImage from '../../../../../product/components/ProductImage/ProductImage';
import { AppContext } from '../../../../../core/context';
import styles from './SearchField.module.scss';

const maxPreviewCategories = 6;
const maxPreviewItems = 4;

export const CategoriesSection = memo(
    ({
        isLoading,
        categories,
        error,
        onClick,
    }: {
        isLoading: boolean;
        categories: { id: string; name: string; parentName?: string }[];
        error?: Error;
        onClick?: () => void;
    }) => {
        if (error) {
            return <UnhandledError noPadding>{error}</UnhandledError>;
        }
        if (isLoading) {
            return (
                <VStack gap="gap16">
                    <Text bold>
                        <FormattedMessage id="categories" />
                    </Text>
                    <ProgressSpinner data-test-class="searchPreviewResultsLoading" />
                </VStack>
            );
        }

        const firstCategories = categories.slice(0, maxPreviewCategories);
        const isEmpty = firstCategories.length === 0;

        if (isEmpty) {
            return null;
        }

        return (
            <VStack gap="gap16">
                <Text bold>
                    <FormattedMessage id="categories" />
                </Text>
                <VStack gap="mini">
                    {firstCategories.map(({ id, name, parentName }) => (
                        <CurrentStoreLink
                            key={id}
                            className={styles.linkItem}
                            to={`/categories/${id}/page/1`}
                            onClick={onClick}
                            data-test-class="searchPreviewCategoryResultItem"
                        >
                            <Text primary hyphenated>
                                {name}
                            </Text>

                            {parentName && (
                                <>
                                    &nbsp;
                                    <Text regular color="gray">
                                        <FormattedMessage id="in" />
                                    </Text>
                                    &nbsp;
                                    <Text regular color="gray" hyphenated>
                                        {parentName}
                                    </Text>
                                </>
                            )}
                        </CurrentStoreLink>
                    ))}
                </VStack>
            </VStack>
        );
    }
);

export const BrandsSection = memo(
    ({
        isLoading,
        brands,
        error,
        onClick,
    }: {
        isLoading: boolean;
        brands: { logo?: string; name: string; slug: string }[];
        error?: Error;
        onClick?: () => void;
    }) => {
        if (error) {
            return <UnhandledError noPadding>{error}</UnhandledError>;
        }
        if (isLoading) {
            return (
                <VStack gap="gap16">
                    <Text bold>
                        <FormattedMessage id="makes" />
                    </Text>
                    <ProgressSpinner data-test-class="searchPreviewResultsLoading" />
                </VStack>
            );
        }

        const firstBrands = brands.slice(0, maxPreviewItems);
        const isEmpty = firstBrands.length === 0;

        if (isEmpty) {
            return null;
        }

        return (
            <VStack gap="gap16">
                <Text bold>
                    <FormattedMessage id="makes" />
                </Text>
                <VStack gap="mini">
                    {firstBrands.map(({ name, slug, logo }) => (
                        <CurrentStoreLink
                            key={slug}
                            className={styles.linkItem}
                            to={`/brand/${slug}`}
                            onClick={onClick}
                            data-test-class="searchPreviewBrandResultItem"
                        >
                            <HStack gap="micro" align="center">
                                <BrandImage
                                    url={logo}
                                    size="80px"
                                    alt={name}
                                    aspectRatio={1 / 2}
                                    mixBlendMode="multiply"
                                />
                                <Text primary>{name}</Text>
                            </HStack>
                        </CurrentStoreLink>
                    ))}
                </VStack>
            </VStack>
        );
    }
);

export const ProductListItem = memo(
    ({
        product,
        onClick,
        query,
    }: {
        product: SearchResultProduct;
        onClick?: () => void;
        query: string;
    }) => {
        const productDetailsLink = useProductDetailsLink(product.id);
        const category = product.categories.at(1) ?? product.categories.at(0);
        const { isInMobileView } = useContext(AppContext);

        const highlightedParts = highlightParse(
            product.name,
            highlightMatch(product.name, query)
        );

        return (
            <CurrentStoreLink
                to={productDetailsLink}
                className={clsx(styles.productItem, {
                    [styles.borderBottom]: !isInMobileView,
                })}
                onClick={onClick}
                data-test-class="searchPreviewProductResultItem"
                data-test-state={product.sku}
            >
                <HStack gap="mini" align="center">
                    <ProductImage
                        size="26px"
                        mediaUrl={product.media[0]?.mediaUrl}
                    />
                    <Text key={product.id}>
                        {highlightedParts.map(({ text, highlight }, idx) => (
                            <Text key={text + idx} bold={highlight}>
                                {text}
                            </Text>
                        ))}
                        {category && (
                            <Text regular color="gray">
                                &nbsp;
                                <FormattedMessage id="in" />
                                &nbsp;
                                {category.name}
                            </Text>
                        )}
                    </Text>
                </HStack>
            </CurrentStoreLink>
        );
    }
);
